/**
 * Authentication context provider
 */

import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, AuthApiError } from './authApi';
import { AuthContext } from './context';
import { clearEmulationData } from '../contexts/EmulationContext';
import { clearDemoMode } from './demoSession';
import { getBrowserTimezone } from '../utils/timezone';
import { clearPersistedQueryCache } from '../offline/queryPersister';
import { SESSION_EXPIRED_EVENT, resetSessionExpiredGuard } from '../api/axiosInstance';
import { AUTH_STORAGE_KEYS } from './storageKeys';
import { isMfaChallenge } from './types';
import type {
  AuthContextType,
  AuthSessionResponse,
  AuthState,
  LoginOutcome,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User } from './types';

// Error codes the server uses to explicitly reject a token. Anything outside
// this set — TypeError from fetch, 5xx, HTML_RESPONSE from a WAF — is treated
// as a transient/network condition and MUST NOT trigger sign-out at bootstrap.
const AUTH_REJECTION_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED',
  'AUTHENTICATION_REQUIRED',
  'INVALID_TOKEN',
  'INVALID_REFRESH_TOKEN',
  'TOKEN_EXPIRED',
]);

function isAuthRejection(err: unknown): boolean {
  return err instanceof AuthApiError && AUTH_REJECTION_CODES.has(err.code);
}

function readStoredUser(): User | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.user);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<User>;
    // Optimistic hydrate relies on these to route the user (ProtectedRoute
    // checks tenant + onboarding). A future User-schema change could leave a
    // stale blob missing fields — fall back to the blocking /me path instead.
    if (typeof parsed?.id !== 'string' || typeof parsed?.tenant_id !== 'string') {
      return null;
    }
    return parsed as User;
  } catch {
    return null;
  }
}

function clearStoredAuth() {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);
  const queryClient = useQueryClient();

  // Purge the React Query cache (in-memory + persisted IndexedDB) on every auth
  // transition. The persisted cache is shared across every user on a browser
  // profile (one PersistQueryClientProvider at the app root) and internal
  // login/logout navigate via SPA navigate() with no full reload — so without
  // this, on a shared/kiosk browser user B would briefly render user A's cached
  // tasks/deals/financial data from cache. `clear()` empties memory
  // synchronously (before the next render); `removeClient()` deletes the
  // IndexedDB copy so a subsequent reload can't restore the stale snapshot.
  // Mirrors the external portal fix (ExternalAuthContext). Returns the promise
  // so identity-swap paths (login) can await the IndexedDB delete before
  // establishing the new session — closing the cross-tab restore race — while
  // full-teardown paths (expiry, bootstrap-reject) fire-and-forget it.
  const purgeQueryCache = useCallback(
    () => clearPersistedQueryCache(queryClient),
    [queryClient],
  );

  // Load stored auth state on mount. Bootstrap is offline-safe: a network error
  // on /me or /refresh must NOT wipe a cached session — only an explicit auth
  // rejection from the server does. The axios 401 interceptor
  // (SESSION_EXPIRED_EVENT) remains the authoritative sign-out path for
  // requests made later from the running app.
  useEffect(() => {
    // Optimistically hydrate from cache, then revalidate /me in the background.
    // Only an explicit auth rejection (from /me AND, if present, /refresh) clears
    // the cached session; everything else keeps the user signed in.
    const revalidateInBackground = async (
      accessToken: string,
      refreshToken: string | null,
    ) => {
      try {
        const currentUser = await authApi.getMe(accessToken);
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
        setState((prev) => {
          // Skip if the session has been replaced (login, logout) since hydrate.
          if (prev.accessToken !== accessToken) return prev;
          return { ...prev, user: currentUser };
        });
        return;
      } catch (meError) {
        if (!isAuthRejection(meError)) return;
      }

      // /me explicitly rejected — try refresh.
      if (refreshToken) {
        try {
          const tokens = await authApi.refresh(refreshToken);
          const currentUser = await authApi.getMe(tokens.access_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
          setState((prev) => {
            if (prev.accessToken !== accessToken) return prev;
            return {
              ...prev,
              user: currentUser,
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
            };
          });
          return;
        } catch (refreshError) {
          // Network/transient on refresh must NOT wipe the session.
          if (!isAuthRejection(refreshError)) return;
        }
      }

      // Both /me and refresh explicitly rejected — real sign-out. The stale
      // token was optimistically hydrated on boot; the persisted cache belongs
      // to this now-rejected user, so purge it before we clear the session.
      clearStoredAuth();
      void purgeQueryCache();
      setState((prev) => {
        if (prev.accessToken !== accessToken) return prev;
        return { ...initialState, isLoading: false };
      });
    };

    const loadStoredAuth = async () => {
      const storedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      const storedRefreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      const storedUser = readStoredUser();

      if (!storedAccessToken) {
        setState({ ...initialState, isLoading: false });
        return;
      }

      // Path A: token + cached user — hydrate immediately so the shell boots
      // offline; revalidate in the background.
      if (storedUser) {
        setState({
          user: storedUser,
          accessToken: storedAccessToken,
          refreshToken: storedRefreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
        void revalidateInBackground(storedAccessToken, storedRefreshToken);
        return;
      }

      // Path B: token but no cached user (e.g. emulation activation cleared it)
      // — we have nothing to render, so /me must succeed before we authenticate.
      try {
        const currentUser = await authApi.getMe(storedAccessToken);
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
        setState({
          user: currentUser,
          accessToken: storedAccessToken,
          refreshToken: storedRefreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      } catch (meError) {
        if (!isAuthRejection(meError)) {
          // Network/transient: keep tokens for a later retry, but we have no
          // user shape to render against — leave isAuthenticated=false. The
          // resulting state is intentionally inconsistent (token present,
          // unauthenticated): a subsequent app foreground / reload will re-run
          // bootstrap once the network is up. See work item AC #4.
          setState({
            user: null,
            accessToken: storedAccessToken,
            refreshToken: storedRefreshToken,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }
      }

      // /me explicitly rejected — try refresh.
      if (storedRefreshToken) {
        try {
          const tokens = await authApi.refresh(storedRefreshToken);
          const currentUser = await authApi.getMe(tokens.access_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);
          localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
          setState({
            user: currentUser,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        } catch (refreshError) {
          if (!isAuthRejection(refreshError)) {
            setState({
              user: null,
              accessToken: storedAccessToken,
              refreshToken: storedRefreshToken,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }
        }
      }

      // Both tokens explicitly rejected on boot — purge the now-rejected user's
      // persisted cache before clearing the session.
      clearStoredAuth();
      void purgeQueryCache();
      setState({ ...initialState, isLoading: false });
    };

    loadStoredAuth();
  }, [purgeQueryCache]);

  // Listen for session expiry from axios interceptor and clear auth state immediately
  useEffect(() => {
    const handleSessionExpired = () => {
      // A revoked/expired session is an auth transition too — purge the cache
      // so the previous user's data doesn't survive to the next login.
      void purgeQueryCache();
      setState({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [purgeQueryCache]);

  // Sync auth state across browser tabs when this tab becomes visible.
  // Uses visibilitychange (fires before focus) so we reload before React Query
  // refetches with a stale/missing token.
  useEffect(() => {
    const syncAuthOnVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      if (storedToken && !state.isAuthenticated) {
        // Another tab logged in — reload to pick up the session cleanly
        window.location.reload();
      } else if (!storedToken && state.isAuthenticated) {
        // Another tab logged out — reload to clear everything
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', syncAuthOnVisible);
    return () => {
      document.removeEventListener('visibilitychange', syncAuthOnVisible);
    };
  }, [state.isAuthenticated]);

  // The one place a session is established from a server response that carries
  // both tokens and the user. Every caller goes through here.
  const writeSession = useCallback(async (response: AuthSessionResponse): Promise<void> => {
    // Purge any prior user's cached data (in-memory + persisted IndexedDB)
    // BEFORE establishing the new session, so the incoming user never renders
    // the previous user's lists from cache on a shared browser. Awaited so the
    // IndexedDB delete completes before we write the new token — otherwise
    // another tab, reloading on the new token via visibilitychange, could
    // restore the prior user's snapshot before removeClient() lands.
    await purgeQueryCache();
    // Write user and refresh token before access token — the storage event
    // listener in other tabs triggers on access_token and reads user/refresh from localStorage
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refresh_token);
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
    resetSessionExpiredGuard();

    setState({
      user: response.user,
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      isAuthenticated: true,
      isLoading: false });
  }, [purgeQueryCache]);

  // Establish a session for a NEW identity (invitation acceptance). Adds the
  // cleanup a login doesn't need: a shared browser may still hold the previous
  // user's live emulation or demo session, neither of which belongs to the
  // incoming user. Cleared BEFORE the session write so no render sees the new
  // user wearing the old one's emulation.
  const establishSessionForNewIdentity = useCallback(
    async (response: AuthSessionResponse): Promise<void> => {
      clearEmulationData();
      clearDemoMode();
      await writeSession(response);
    },
    [writeSession],
  );

  // Establish a session from a completed-login response. Shared by password
  // login and the MFA challenge flow (the /mfa/verify response has this shape).
  const completeMfaLogin = useCallback(async (response: LoginResponse): Promise<string | null> => {
    await writeSession(response);
    // Return redirect URL if user should be redirected to their custom domain
    return response.redirect_to_domain || null;
  }, [writeSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<LoginOutcome> => {
    const browserTimezone = getBrowserTimezone();
    const result = await authApi.login({ ...credentials, timezone: browserTimezone });

    // MFA required: no JWT issued yet — hand the challenge back to the caller.
    if (isMfaChallenge(result)) {
      return { kind: 'mfa', challenge: result };
    }

    return { kind: 'authenticated', redirectUrl: await completeMfaLogin(result) };
  }, [completeMfaLogin]);

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    // Registration now creates a pending user that needs email verification
    // No auto-login - user must verify email first
    const response = await authApi.register(data);
    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (state.accessToken) {
        await authApi.logout(state.accessToken);
      }
    } catch {
      // Ignore logout errors
    }

    localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.user);

    // Clear any active emulation session
    clearEmulationData();
    // Clear any active read-only demo session
    clearDemoMode();
    // Drop this user's cached data so the next user (or a logged-out shared
    // device) can't see it from the persisted query cache.
    await purgeQueryCache();

    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false });
  }, [state.accessToken, purgeQueryCache]);

  const refreshAuth = useCallback(async () => {
    // Read from localStorage to get the latest token (may have been updated externally, e.g., by setup wizard)
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken) || state.accessToken;
    const emulationToken = localStorage.getItem(AUTH_STORAGE_KEYS.emulationToken);

    // During an active sysadmin emulation the stored refresh token still belongs
    // to the sysadmin — emulation only swaps the access token (see EmulationContext),
    // there is no emulation refresh token. Exchanging it here would mint a fresh
    // sysadmin access token and silently drop the operator out of emulation (the
    // avatar flips back to the sysadmin, the emulated tenant's data vanishes). This
    // is exactly what happened when saving certain tenant settings (e.g. default
    // language) called refreshAuth(). Instead, re-fetch the emulated user with the
    // existing emulation access token so callers still observe server-side changes,
    // while leaving the emulation session intact.
    if (emulationToken && accessToken && emulationToken === accessToken) {
      const user = await authApi.getMe(accessToken);
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));
      setState((prev) => ({
        ...prev,
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      }));
      return;
    }

    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) || state.refreshToken;
    if (!refreshToken) {
      throw new AuthApiError('NO_REFRESH_TOKEN', 'No refresh token available');
    }

    const tokens = await authApi.refresh(refreshToken);
    const user = await authApi.getMe(tokens.access_token);

    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));

    setState({
      user,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      isAuthenticated: true,
      isLoading: false });
  }, [state.refreshToken, state.accessToken]);

  const updateUser = useCallback(async (data: Parameters<typeof authApi.updateMe>[1]) => {
    if (!state.accessToken) {
      throw new AuthApiError('NO_ACCESS_TOKEN', 'No access token available');
    }

    try {
      const updatedUser = await authApi.updateMe(state.accessToken, data);

      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(updatedUser));

      setState((prev) => ({
        ...prev,
        user: updatedUser,
      }));

      return updatedUser;
    } catch (error) {
      // If 401 and we have a refresh token, try to refresh and retry
      // Backend returns AUTHENTICATION_REQUIRED for 401 errors
      if (error instanceof AuthApiError && (error.code === 'UNAUTHORIZED' || error.code === 'AUTHENTICATION_REQUIRED') && state.refreshToken) {
        const tokens = await authApi.refresh(state.refreshToken);

        localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
        localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);

        // Retry with new token
        const updatedUser = await authApi.updateMe(tokens.access_token, data);

        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(updatedUser));

        setState((prev) => ({
          ...prev,
          user: updatedUser,
        }));

        return updatedUser;
      }
      throw error;
    }
  }, [state.accessToken, state.refreshToken]);

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      completeMfaLogin,
      establishSessionForNewIdentity,
      register,
      logout,
      refreshAuth,
      updateUser,
    }),
    [state, login, completeMfaLogin, establishSessionForNewIdentity, register, logout, refreshAuth, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthApiError };
